/**
 *  @class
 *  @function Predictive Search
 *  Anpassad: 
 *   - Söker bara produkter (inga collections/articles/query)
 *   - Bygger “smart query” med flera format (EAN/SKU/med-utan bindestreck/mellanslag/tokeniserat)
 *   - Söker även på variants.barcode (EAN)
 */
if (!customElements.get('search-form')) {
  class SearchForm extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.form = this.querySelector('form');
      this.input = this.querySelector('input[type="search"]');
      this.predictiveSearchResults = this.querySelector('.thb-predictive-search');
      this.cc = this.querySelector('.searchform--click-capture');

      this.setupEventListeners();
    }

    setupEventListeners() {
      this.form.addEventListener('submit', this.onFormSubmit.bind(this));
      this.predictiveSearchResults.addEventListener('focusout', this.close.bind(this));

      this.input.addEventListener(
        'input',
        debounce(() => {
          this.onChange();
        }, 300).bind(this)
      );

      this.cc.addEventListener('click', this.close.bind(this));

      document.addEventListener('keyup', (e) => {
        if (e.code && e.code.toUpperCase() === 'ESCAPE') {
          this.close();
        }
      });

      this.input.addEventListener('focus', this.open.bind(this));
    }

    getQuery() {
      return this.input.value.trim();
    }

    onChange() {
      const searchTerm = this.getQuery();

      if (!searchTerm.length) {
        document.body.classList.remove('open-search');
        this.predictiveSearchResults.setAttribute('inert', '');
        return;
      }

      document.body.classList.add('open-search');
      this.predictiveSearchResults.removeAttribute('inert');
      this.getSearchResults(searchTerm);
    }

    onFormSubmit(event) {
      if (!this.getQuery().length) {
        event.preventDefault();
      }
    }

    onFocus() {
      const searchTerm = this.getQuery();
      if (!searchTerm.length) return;
      this.getSearchResults(searchTerm);
    }

    // Bygger flera format av samma input och gör en OR-query
    buildSmartQuery(searchTerm) {
      const raw = (searchTerm || '').trim();
      if (!raw) return '';

      const lower = raw.toLowerCase();

      // Ta bort separators: mellanslag, bindestreck, underscore, punkt, slash
      const compact = lower.replace(/[\s\-_./]+/g, '');

      // Endast siffror (EAN / numerisk SKU)
      const digitsOnly = lower.replace(/\D+/g, '');

      // Tokenisera mellan bokstäver/siffror: "ms400c-mw" -> "ms 400 c mw"
      const tokenized = lower
        .replace(/[\s\-_./]+/g, ' ')
        .replace(/([a-zåäö])(\d)/gi, '$1 $2')
        .replace(/(\d)([a-zåäö])/gi, '$1 $2')
        .replace(/\s+/g, ' ')
        .trim();

      const variants = new Set();
      const add = (v) => {
        const s = String(v || '').trim();
        if (s) variants.add(s);
      };

      add(raw);
      add(lower);
      add(compact);
      add(tokenized);

      // EAN/siffror: prova även split -2 (vanligt i artikelnummer)
      if (digitsOnly.length >= 6) {
        add(digitsOnly);
        add(`${digitsOnly.slice(0, -2)}-${digitsOnly.slice(-2)}`);
        add(`${digitsOnly.slice(0, -2)} ${digitsOnly.slice(-2)}`);
      }

      // Begränsa så det inte blir segt
      const queryParts = Array.from(variants).slice(0, 8);

      // Citat runt kod-lika termer så '-' inte tolkas som NOT i Shopify search-syntax
      const quoteIfNeeded = (s) => {
        const hasSpecial = /[-_./]/.test(s);
        const hasSpace = /\s/.test(s);
        if (hasSpecial && !hasSpace) return `"${s.replace(/"/g, '\\"')}"`;
        return s;
      };

      return queryParts.length > 1
        ? queryParts.map((p) => `(${quoteIfNeeded(p)})`).join(' OR ')
        : quoteIfNeeded(queryParts[0]);
    }

    getSearchResults(searchTerm) {
      this.predictiveSearchResults.classList.add('loading');

      const q = this.buildSmartQuery(searchTerm);

      fetch(
        `${theme.routes.predictive_search_url}?q=${encodeURIComponent(q)}&${encodeURIComponent(
          'resources[type]'
        )}=product&${encodeURIComponent('resources[limit]')}=10&resources[options][fields]=title,product_type,vendor,variants.title,variants.sku,variants.barcode&resources[options][prefix]=last&section_id=predictive-search`
      )
        .then((response) => {
          this.predictiveSearchResults.classList.remove('loading');
          if (!response.ok) {
            throw new Error(response.status);
          }
          return response.text();
        })
        .then((text) => {
          const doc = new DOMParser().parseFromString(text, 'text/html');
          const section = doc.querySelector('#shopify-section-predictive-search');
          const resultsMarkup = section ? section.innerHTML : '';
          this.renderSearchResults(resultsMarkup);
        })
        .catch((error) => {
          this.predictiveSearchResults.classList.remove('loading');
          throw error;
        });
    }

    renderSearchResults(resultsMarkup) {
      this.predictiveSearchResults.innerHTML = resultsMarkup;

      const submitButton = this.querySelector('#search-results-submit');
      if (submitButton) {
        submitButton.addEventListener('click', () => {
          this.form.submit();
        });
      }
    }

    close() {
      this.predictiveSearchResults.setAttribute('inert', '');
      document.body.classList.remove('open-search');
    }

    open() {
      document.body.classList.add('open-search');
      this.predictiveSearchResults.removeAttribute('inert');
    }
  }

  customElements.define('search-form', SearchForm);
}
